import {Assignment, AssignmentPlan, assignmentsFromLegs, assignmentPlanForLegs, planAssignments, legsFromAssignmentPlan} from './assignment-plan';
import {visitsFromIDs, RouteVisit} from './route-visits';
import {FlightLeg} from "schema";
import {FlightPath, FlightPathOptionalArgs} from "./flight-path";

export interface FlightPax {
    _id: string,
    departureID: string,
    destinationID: string,
    paxWeight: number,
    bagWeight: number,
    bagCount: number
}

export interface FlightCgo {
    _id: string,
    departureID: string,
    destinationID: string,
    weight: number
}

type RouteRequirement = Pick<FlightPax | FlightCgo, 'departureID' | 'destinationID'>;

/**
 * Builds a directed Euler trail when every requested movement can be flown
 * exactly once from the selected flight origin. This is the ideal case for a
 * new flight: it avoids empty positioning legs and keeps each requested
 * departure/destination pair adjacent in the generated route.
 *
 * Duplicate movements are collapsed because several passengers or pieces of
 * cargo travelling between the same locations only require one flight leg.
 */
export function buildDirectRouteFromRequirements(
    origin: string,
    requirements: RouteRequirement[]
): string[] | undefined {
    if (!origin) return undefined;

    const uniqueEdges = Array.from(new Map(
        requirements
            .filter(({departureID, destinationID}) => (
                !!departureID && !!destinationID && departureID !== destinationID
            ))
            .map(({departureID, destinationID}) => [
                `${departureID}\u0000${destinationID}`,
                {departureID, destinationID}
            ])
    ).values());

    if (!uniqueEdges.length) return undefined;

    const outgoing = new Map<string, string[]>();
    const inDegree = new Map<string, number>();
    const outDegree = new Map<string, number>();
    const neighbors = new Map<string, Set<string>>();
    const vertices = new Set<string>();

    const addNeighbor = (from: string, to: string) => {
        if (!neighbors.has(from)) neighbors.set(from, new Set());
        neighbors.get(from).add(to);
    };

    uniqueEdges.forEach(({departureID, destinationID}) => {
        vertices.add(departureID);
        vertices.add(destinationID);
        outgoing.set(departureID, [...(outgoing.get(departureID) || []), destinationID]);
        outDegree.set(departureID, (outDegree.get(departureID) || 0) + 1);
        inDegree.set(destinationID, (inDegree.get(destinationID) || 0) + 1);
        addNeighbor(departureID, destinationID);
        addNeighbor(destinationID, departureID);
    });

    if (!vertices.has(origin) || !(outDegree.get(origin) || 0)) return undefined;

    // Euler paths require every location that participates in a requested
    // movement to belong to one connected route.
    const visited = new Set<string>();
    const pending = [origin];
    while (pending.length) {
        const current = pending.pop();
        if (visited.has(current)) continue;
        visited.add(current);
        neighbors.get(current)?.forEach((neighbor) => {
            if (!visited.has(neighbor)) pending.push(neighbor);
        });
    }
    if (visited.size !== vertices.size) return undefined;

    let endLocationCount = 0;
    let hasOpenTrailStart = false;
    for (const vertex of Array.from(vertices)) {
        const difference = (outDegree.get(vertex) || 0) - (inDegree.get(vertex) || 0);
        if (difference === 1) {
            if (vertex !== origin || hasOpenTrailStart) return undefined;
            hasOpenTrailStart = true;
        }
        else if (difference === -1) {
            endLocationCount++;
        }
        else if (difference !== 0) {
            return undefined;
        }
    }
    if (hasOpenTrailStart ? endLocationCount !== 1 : endLocationCount !== 0) return undefined;

    // Sort before walking so equivalent valid graphs always produce the same
    // route regardless of CouchDB or GraphQL result ordering.
    outgoing.forEach((destinations) => destinations.sort((a, b) => b.localeCompare(a)));

    const stack = [origin];
    const reversedRoute: string[] = [];
    while (stack.length) {
        const current = stack[stack.length - 1];
        const destinations = outgoing.get(current);
        if (destinations?.length) {
            stack.push(destinations.pop());
        }
        else {
            reversedRoute.push(stack.pop());
        }
    }

    const route = reversedRoute.reverse();
    return route.length === uniqueEdges.length + 1 ? route : undefined;
}

/**
 * Algorithm that finds the shortest subset of locations a pax can visit.
 * @param path List of locations a flight will visit
 * @param startLoc Departing location of the pax
 * @param endLoc Destination of the pax
 * @returns [startIdx, endIdx]
 */
export function getOptimalFlightPathSubset(path: string[], startLoc: string, endLoc: string){
    // start index and end index
    let si = 0;
    let ei = 1;

    // Last start index and last end index
    let lsi = -Infinity;
    let lei = 0;

    let moveStart = false;

    let loops = 0;
    let maxLoops = 10000;

    while (si < path.length - 1 && ei < path.length){
        loops++;
        if (loops > maxLoops){
            throw Error("Max loops exceeded.");
        }
        if (path[ei] === endLoc && ei > lei && moveStart === false){
            moveStart = true;
        }
        if (si === ei-1 && moveStart === true){
            // Start index is right behind end index. Cannot move further.
            moveStart = false;
        }
        if (path[ei] === endLoc && path[si] === startLoc && ei-si < lei-lsi){
            lsi = si;
            lei = ei;
            continue;
        }

        if (moveStart)
            si++;
        else
            ei++;
    }

    return [lsi, lei]
}

function updateEntityDepDest(originalOrigin: string, overrideOrigin: string, entity: FlightPax | FlightCgo){
    let entityCopy = {...entity};
    if (originalOrigin === overrideOrigin) return entityCopy;

    if (entityCopy.departureID === originalOrigin){
        entityCopy.departureID = overrideOrigin;
    }
    if (entityCopy.destinationID === originalOrigin){
        entityCopy.destinationID = overrideOrigin;
    }
    return entityCopy;
}

type FlightLegManagerArgsBase  = {
    /**
     * Maps the location ID to a human-readable name. REQUIRED FOR 'departure' and 'destination' fields in flight legs to be populated!!!
     */
    locationIDToNameMap?: Map<string, string>,

    /**
     * Optional arguments to pass to the FlightPath
     */
    flightPathOptionalArgs?: FlightPathOptionalArgs,

    /**
     * Override the origin location of the flight path.
     */
    overrideOrigin?: string
}

export type FlightLegManagerArgs = ({

    /**
     * Set the initial flight legs
     */
    initialLegs: FlightLeg[],

    /**
     * Pax objects in initialLegs
     */
    paxObjs: FlightPax[],

    /**
     * Cgo objects in initialLegs
     */
    cgoObjs: FlightCgo[],
} & FlightLegManagerArgsBase) | FlightLegManagerArgsBase

export type ReorderFlightLegsResult = {
    error: string;
} | {
    legs: FlightLeg[];
    paxIds: string[];
    cgoIds: string[];
}

class FlightLegManager {

    flightPath: FlightPath;
    initialLegs: FlightLeg[];
    allPax: Map<string, FlightPax> = new Map();
    allCgo: Map<string, FlightCgo> = new Map();
    private savedAssignments: Assignment[] = [];
    assignmentPlan: AssignmentPlan;
    bindInitialVisits(visits: RouteVisit[]) {
        this.savedAssignments = assignmentsFromLegs(this.initialLegs || [], visits);
    }
    setRouteVisits(visits: RouteVisit[]) {
        this.flightPath = FlightPath.fromLocationList(visits.map(visit => ({_id: visit.locationID})));
        this.flightPath.setVisits(visits);
    }
    private locIDToNameMap: Map<string, string> = new Map();

    private manAssignedPax = new Map<string, [string, string]>();
    private manAssignedCgo = new Map<string, [string, string]>();

    constructor(args: FlightLegManagerArgs){

        if (args.locationIDToNameMap){
            this.locIDToNameMap = args.locationIDToNameMap;
        }

        if ('initialLegs' in args){
            this.initialLegs = args.initialLegs;
            const inherited = assignmentPlanForLegs(args.initialLegs);
            const originalVisits = inherited?.visits || visitsFromIDs(args.initialLegs.length
                ? [args.initialLegs[0].departureID, ...args.initialLegs.map(leg => leg.destinationID)] : []);
            this.savedAssignments = inherited?.rows || assignmentsFromLegs(args.initialLegs, originalVisits);
            this.flightPath = FlightPath.fromFlightLegs(args.initialLegs, args.overrideOrigin, args.flightPathOptionalArgs);
            const oldOrigin = args.initialLegs[0]?.departureID;
            this.flightPath.setVisits(originalVisits.map(visit => ({...visit, locationID:
                args.overrideOrigin && visit.locationID === oldOrigin ? args.overrideOrigin : visit.locationID})));
            args.paxObjs.forEach((obj) => {
                let newObj = updateEntityDepDest(this.flightPath.getOriginalOrigin(), this.flightPath.getOrigin(), obj) as FlightPax;
                this.allPax.set(obj._id, newObj);
            })
            args.cgoObjs.forEach((obj) => {
                let newObj = updateEntityDepDest(this.flightPath.getOriginalOrigin(), this.flightPath.getOrigin(), obj) as FlightCgo;
                this.allCgo.set(obj._id, newObj);
            })
            const missing = this.savedAssignments.filter(assignment =>
                !(assignment.kind === 'pax' ? this.allPax : this.allCgo).has(assignment.id));
            if (missing.length) throw new Error('Some assigned PAX/CGO could not be loaded. Reload the flight before editing its route.');
        }
        else {
            this.flightPath = new FlightPath(args.overrideOrigin, args.flightPathOptionalArgs);
        }
    }

    // public insertPaxCgo(
    //     departureID: string,
    //     destinationID: string,
    //     paxObjs: FlightPax[],
    //     cgoObjs: FlightCgo[]
    //     ){
    //         this.flightPath.insertNode(departureID, destinationID);
    //         paxObjs.forEach((obj) => this.allPax.set(obj._id, obj))
    //         cgoObjs.forEach((obj) => this.allCgo.set(obj._id, obj))
    // }

    // Sees if a passenger's departure and destination includes this flight leg
    isOnLeg(pax: FlightPax | FlightCgo, legDepIdx: number, legDestIdx: number){
        let paxDep = pax.departureID;
        let paxDest = pax.destinationID;
        const path = this.flightPath.getRoutePath();
        const [ startIdx, endIdx ] = getOptimalFlightPathSubset(path, paxDep, paxDest);

        const isOnLeg = (
            startIdx > -1 && endIdx > -1 &&
            legDepIdx >= startIdx &&
            endIdx >= legDestIdx
        );

        return isOnLeg;
    }

    private cleanRedundantNodes(){
        let scanned = new Set();
        Array.from(this.allPax.values()).forEach((pax) => {
            scanned.add(pax.departureID);
            scanned.add(pax.destinationID);
        })
        Array.from(this.allCgo.values()).forEach((cgo) => {
            scanned.add(cgo.departureID);
            scanned.add(cgo.destinationID);
        })

        let redundantNodes = [];

        let path = this.flightPath.getPath();
        path.forEach((node) => {
            if (!scanned.has(node)){
                redundantNodes.push(node);
            }
        })

        redundantNodes.forEach((node) => this.flightPath.removeNode(node))
    }

    /**
     * Checks if a location in the flight path is no longer needed.
     * @param locID 
     * @returns true if the leg is redundant. False if not redundant.
     */
    public isLocationRedundant(locID: string){
        if (!locID) return false;
        let scanned = new Set();

        if (this.getOrigin() === locID){
            // Locations that are the origin of the flight path
            // are not redundant, even though no pax/cgo are flying on it
            return false;
        }

        Array.from(this.allPax.values()).forEach((pax) => {
            scanned.add(pax.departureID);
            scanned.add(pax.destinationID);
        })
        Array.from(this.allCgo.values()).forEach((cgo) => {
            scanned.add(cgo.departureID);
            scanned.add(cgo.destinationID);
        })
        if (!scanned.has(locID)){
            return true;
        }
        return false;
    }

    /**
     * Removes a node from the flight path if it is not in anyone's flight path
     * @param locID 
     * @returns true if leg was removed. False if leg still remains.
     */
    public removeLocationIfRedundant(locID: string){
        if (this.isLocationRedundant(locID)){
            this.flightPath.removeNode(locID);
            return true;
        }
        return false;
    }

    /**
     * Checks whether the destination reached by a specific leg occurrence can
     * be removed while every passenger and cargo movement remains possible.
     * This differs from isLocationRedundant because a route may visit the same
     * required location more than once, with only one visit being necessary.
     */
    public isLegRedundant(legIndex: number) {
        const route = this.flightPath.getRoutePath();
        if (legIndex < 0 || legIndex >= route.length - 1) return false;

        const finalLegIndex = route.length - 2;
        const hasFinalReturn = route[route.length - 1] === this.getOrigin();
        if (hasFinalReturn && legIndex === finalLegIndex) return false;

        const candidateRoute = [...route];
        candidateRoute.splice(legIndex + 1, 1);

        const requirements: RouteRequirement[] = [
            ...Array.from(this.allPax.values()),
            ...Array.from(this.allCgo.values())
        ];
        return requirements.every(({departureID, destinationID}) => {
            const [startIndex, endIndex] = getOptimalFlightPathSubset(
                candidateRoute,
                departureID,
                destinationID
            );
            return Number.isFinite(startIndex) &&
                Number.isFinite(endIndex) &&
                startIndex >= 0 &&
                endIndex > startIndex;
        });
    }

    /** Removes one redundant route occurrence without affecting later visits. */
    public removeLegIfRedundant(legIndex: number) {
        if (!this.isLegRedundant(legIndex)) return false;
        return !this.flightPath.removeDestinationAtIndex(legIndex);
    }

    addPassenger(pax: FlightPax, args?: { updateFlightPath?: boolean }){
        this.allPax.set(pax._id, pax);
        if (typeof args?.updateFlightPath === 'boolean' ? args.updateFlightPath : true){
            this.flightPath.insertNode(pax.departureID, pax.destinationID);
        }
    }

    addCgo(cgo: FlightCgo, args?: { updateFlightPath?: boolean }){
        this.allCgo.set(cgo._id, cgo);
        if (typeof args?.updateFlightPath === 'boolean' ? args.updateFlightPath : true) {
            this.flightPath.insertNode(cgo.departureID, cgo.destinationID);
        }
    }

    removePassenger(id: string){
        // let pax = this.allPax.get(id);
        this.allPax.delete(id);
        this.savedAssignments = this.savedAssignments.filter(a => a.kind !== 'pax' || a.id !== id);
        // this.removeLocationIfRedundant(pax.destinationID);
    }

    removeCgo(id: string){
        // let cgo = this.allCgo.get(id);
        this.allCgo.delete(id);
        this.savedAssignments = this.savedAssignments.filter(a => a.kind !== 'cgo' || a.id !== id);
        // this.removeLocationIfRedundant(cgo.destinationID);
    }

    doesEntityHaveALeg(entity: FlightPax | FlightCgo) {
        let subset = this.flightPath.getNodeSubset(entity.departureID, entity.destinationID);
        if (!subset || subset.length === 0){
            return false;
        }
        return true;
    }

    // If two destinations, A → B, are reordered to B → A, and a TRANSFER pax/cgo travels from A → B, and A and B do not contain an origin,
    // then this pax/cgo no longer has a viable path in the flight path.
    // What we need to do is detect these pax/cgo that are in this situation and add a NEW leg that goes from A → B for them.
    // Thus, this will create a third destination with the path B → A → B
    private findAndCreateMissingLegs(){
        const putDestConditionally = (entity: FlightPax | FlightCgo, kind: 'pax' | 'cgo') => {
            // Existing selections are authoritative, including manual overrides and
            // explicit unassignment in a local preview. Only new entities extend routes.
            if (this.savedAssignments.some(a => a.id === entity._id && a.kind === kind)) return;
            let hasLeg = this.doesEntityHaveALeg(entity);
            if (!hasLeg){
                this.flightPath.insertNode(entity.departureID, entity.destinationID);
            }
        }

        let pax = Array.from(this.allPax.values());
        let cgo = Array.from(this.allCgo.values());

        pax.forEach(entity => putDestConditionally(entity, 'pax'));
        cgo.forEach(entity => putDestConditionally(entity, 'cgo'));
    }

    /**
     * Plans a new route from all passenger and cargo requirements at once.
     * Existing routes are intentionally excluded: auto-assign must not reorder
     * a route that a dispatcher has already reviewed or arranged manually.
     */
    private createDirectInitialRoute(){
        if (this.initialLegs?.length || this.flightPath.getPath().length > 1) return false;

        const route = buildDirectRouteFromRequirements(
            this.getOrigin(),
            [
                ...Array.from(this.allPax.values()),
                ...Array.from(this.allCgo.values())
            ]
        );
        if (!route) return false;

        // Keep every occurrence explicit. An Euler circuit can revisit the
        // origin before its final leg, which the regular location-list helper
        // intentionally treats as an implicit return.
        const path = new FlightPath(route[0], {returnToOrigin: false});
        route.slice(1).forEach((locationID) => path.addNextNode(locationID, true));
        this.flightPath = path;
        return true;
    }

    findUnassigned(){
        let paxList = Array.from(this.allPax.values());
        let cgoList = Array.from(this.allCgo.values());

        return {
            pax: paxList.filter((pax) => !this.doesEntityHaveALeg(pax)),
            cgo: cgoList.filter((cgo) => !this.doesEntityHaveALeg(cgo))
        }
    }

    buildFlightLegs(args?: {
        disableCreateMissingLegs: boolean,
        getInfo?: (infoData: {
            paxIds?: string[],
            cgoIds?: string[]
        }) => void
    }): FlightLeg[] {

        if (!args?.disableCreateMissingLegs){
            if (!this.createDirectInitialRoute()) {
                this.findAndCreateMissingLegs();
            }
        }

        this.assignmentPlan = planAssignments(this.flightPath.getVisits(),
            Array.from(this.allPax.values()), Array.from(this.allCgo.values()), this.savedAssignments);
        const legs = legsFromAssignmentPlan(this.assignmentPlan, this.locIDToNameMap, true);
        if (args?.getInfo) args.getInfo({
            paxIds: Array.from(new Set(legs.flatMap(leg => leg.paxIDs))),
            cgoIds: Array.from(new Set(legs.flatMap(leg => leg.cgoIDs)))
        });
        return legs;
    }

    getPaxIDList(){
        return Array.from(this.allPax.values())
            .map(obj => obj._id)
    }

    getCgoIDList(){
        return Array.from(this.allCgo.values())
            .map(obj => obj._id)
    }

    moveDestination(grabDest: string, hoverDest: string){
        // let error = this.flightPath.moveNode(grabDest, hoverDest);
        // if (!error){
        //     // Reodering may cause some nodes in the flight route to become redundant.
        //     this.cleanRedundantNodes();
        // }
        return this.flightPath.moveNode(grabDest, hoverDest);
    }

    moveDestinationAtIndex(fromLegIndex: number, toLegIndex: number){
        return this.flightPath.moveDestinationAtIndex(fromLegIndex, toLegIndex);
    }

    /**
     * Applies the canonical route move and immediately rebuilds every manifest.
     * Both the auto-assign preview and the persisted editor use this command so
     * repeated locations, round-trip closure rules, and missing-leg handling
     * cannot drift between the two interfaces.
     */
    reorderAndBuildFlightLegs(
        fromLegIndex: number,
        toLegIndex: number,
        args?: {createMissingLegs?: boolean}
    ): ReorderFlightLegsResult {
        const error = this.moveDestinationAtIndex(fromLegIndex, toLegIndex);
        if (error) return {error};

        let paxIds: string[] = [];
        let cgoIds: string[] = [];
        const legs = this.buildFlightLegs({
            disableCreateMissingLegs: args?.createMissingLegs === false,
            getInfo: (info) => {
                paxIds = info.paxIds || [];
                cgoIds = info.cgoIds || [];
            }
        });

        return {legs, paxIds, cgoIds};
    }

    getOrigin(){
        return this.flightPath.getOrigin();
    }

    setOrigin(locID: string){
        this.flightPath.setOrigin(locID);
    }

    getLocationName(locID: string){
        return this.locIDToNameMap.get(locID);
    }
}

export default FlightLegManager
